import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';


// Mock Data
const ACTIVE_ENGINES = [
  { student: "Arjun Mehta", focus: "Physics Core Focus", phase: "Week 3 of 6", progress: 45, status: "Active" },
  { student: "Sneha Iyer", focus: "Math Remedial", phase: "Week 1 of 4", progress: 12, status: "Active" },
  { student: "Rohan Das", focus: "Chem Advanced Prep", phase: "Week 8 of 8", progress: 95, status: "Nearing Completion" },
];

const CONTENT_RECOMMENDATIONS = [
  { trigger: "Low score in Mechanics Mock #2", action: "Deploying 5-part Kinematics Video Series", impact: "High" },
  { trigger: "High speed, low accuracy in Math", action: "Throttling mock test time limits by +15%", impact: "Medium" },
  { trigger: "Skipped 3 consecutive Chemistry chapters", action: "Generating warning email to Parent Module", impact: "Critical" },
];

export default function Personalization() {
  const [activeTab, setActiveTab] = useState('AI');
  const [themeColor, setThemeColor] = useState('#06b6d4');
  const [logoUrl, setLogoUrl] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Razorpay & GST
  const [razorpayKeyId, setRazorpayKeyId] = useState('');
  const [razorpayKeySecret, setRazorpayKeySecret] = useState('');
  const [gstNumber, setGstNumber] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [sacCode, setSacCode] = useState('9992');
  const [instituteAddress, setInstituteAddress] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [invoicePrefix, setInvoicePrefix] = useState('PS');
  const [instituteEmail, setInstituteEmail] = useState('');
  const [institutePhone, setInstitutePhone] = useState('');

  const fetchTheme = useCallback(async () => {
    try {
      const res = await axios.get(API_BASE_URL + '/api/theme');
      if (res.data) {
        if (res.data.themeColor) setThemeColor(res.data.themeColor);
        if (res.data.logoUrl) setLogoUrl(res.data.logoUrl);
        if (res.data.schoolName) setSchoolName(res.data.schoolName);
        if (res.data.latitude) setLatitude(res.data.latitude);
        if (res.data.longitude) setLongitude(res.data.longitude);
        
        if (res.data.razorpayKeyId) setRazorpayKeyId(res.data.razorpayKeyId);
        if (res.data.razorpayKeySecret) setRazorpayKeySecret(res.data.razorpayKeySecret);
        if (res.data.gstNumber) setGstNumber(res.data.gstNumber);
        if (res.data.panNumber) setPanNumber(res.data.panNumber);
        if (res.data.sacCode) setSacCode(res.data.sacCode);
        if (res.data.instituteAddress) setInstituteAddress(res.data.instituteAddress);
        if (res.data.stateCode) setStateCode(res.data.stateCode);
        if (res.data.invoicePrefix) setInvoicePrefix(res.data.invoicePrefix);
        if (res.data.instituteEmail) setInstituteEmail(res.data.instituteEmail);
        if (res.data.institutePhone) setInstitutePhone(res.data.institutePhone);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchTheme();
  }, [fetchTheme]);



  const saveBrandingParams = async () => {
    setIsSaving(true);
    try {
      const payload = { 
        themeColor, logoUrl, schoolName,
        razorpayKeyId, razorpayKeySecret, 
        gstNumber, panNumber, sacCode, instituteAddress, stateCode, invoicePrefix,
        instituteEmail, institutePhone,
        latitude, longitude
      };
      await axios.put(API_BASE_URL + '/api/theme', payload);
      toast("Institute branding and payment settings deployed globally.");
    } catch (err) {
      console.error(err);
      toast("Failed to update branding settings.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', 'Institution Logo');
    formData.append('subject', 'Branding');
    formData.append('type', 'LOGO');

    const toastId = toast.loading('Uploading logo...');
    try {
      const res = await axios.post(`${API_BASE_URL}/api/files/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${user?.token}`
        }
      });
      
      const fileUrl = `${API_BASE_URL}/api/files/view/${res.data.id}`;
      setLogoUrl(fileUrl);
      
      toast.update(toastId, { 
        render: 'Logo uploaded successfully', 
        type: 'success', 
        isLoading: false, 
        autoClose: 3000 
      });
    } catch (err) {
      toast.update(toastId, { 
        render: 'Upload failed: ' + (err.response?.data || err.message), 
        type: 'error', 
        isLoading: false, 
        autoClose: 3000 
      });
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 pb-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-3">
            Institute Customizer
          </h1>
          <p className="mt-2 text-md text-slate-400 max-w-2xl">
            Configure automated learning paths or deploy custom White-Label branding layouts across your partition.
          </p>
        </div>
        
        <div className="flex bg-slate-900/50 p-1.5 rounded-xl border border-slate-800 shrink-0">
          <button onClick={() => setActiveTab('AI')} className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'AI' ? 'bg-slate-800 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}>AI Rules</button>
          <button onClick={() => setActiveTab('BRAND')} className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'BRAND' ? 'bg-slate-800 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}>Branding</button>
          <button onClick={() => setActiveTab('PAYMENT')} className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'PAYMENT' ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-900/20' : 'text-slate-400 hover:text-slate-200'}`}>Payment & GST</button>
        </div>
      </div>

      {activeTab === 'AI' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Left Column: Macro Engine Controls */}
          <div className="space-y-6">
            
            <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6">
              <h2 className="text-xl font-semibold text-slate-200 mb-6">Global Algorithm Settings</h2>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 flex-col sm:flex-row gap-4">
                  <div>
                    <h3 className="font-semibold text-slate-200">Adaptive Test Difficulty</h3>
                    <p className="text-xs text-slate-400 mt-1">Scale difficulty based on previous 3 test results.</p>
                  </div>
                  <div className="w-12 h-6 bg-cyan-600 rounded-full relative cursor-pointer"><div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5 shadow"></div></div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/30 border border-slate-700/50 flex-col sm:flex-row gap-4">
                  <div>
                    <h3 className="font-semibold text-slate-200">Smart Content Routing</h3>
                    <p className="text-xs text-slate-400 mt-1">Automatically assign remedial videos for weak topics.</p>
                  </div>
                  <div className="w-12 h-6 bg-cyan-600 rounded-full relative cursor-pointer"><div className="w-5 h-5 bg-white rounded-full absolute right-0.5 top-0.5 shadow"></div></div>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-slate-800 bg-slate-900/40 p-6 relative overflow-hidden">
              <h2 className="text-xl font-semibold text-slate-200 mb-6">Automated Workflows Logging</h2>
              
              <div className="space-y-3 relative z-10">
                {CONTENT_RECOMMENDATIONS.map((rec, idx) => (
                  <div key={idx} className="p-4 rounded-xl border border-slate-800 bg-slate-800/20">
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Trigger Event</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                        rec.impact === 'Critical' ? 'bg-rose-500/10 text-rose-400' : 
                        rec.impact === 'High' ? 'bg-orange-500/10 text-orange-400' : 
                        'bg-blue-500/10 text-blue-400'
                      }`}>{rec.impact} Priority</span>
                    </div>
                    <p className="text-sm font-medium text-slate-300 mb-3">{rec.trigger}</p>
                    
                    <div className="flex items-start gap-3 mt-3 pt-3 border-t border-slate-800/50">
                      <div className="pt-0.5 text-cyan-500">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 10 4 15 9 20"></polyline><path d="M20 4v7a4 4 0 0 1-4 4H4"></path></svg>
                      </div>
                      <p className="text-sm text-cyan-300/90 font-medium">{rec.action}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Right Column: User Tracking */}
          <div className="rounded-3xl border border-cyan-500/20 bg-gradient-to-b from-cyan-950/20 to-slate-900/40 p-6 relative overflow-hidden">
             <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 blur-[60px] rounded-full"></div>
             
             <div className="flex items-center justify-between mb-6 relative z-10">
               <h2 className="text-xl font-semibold text-cyan-200 flex items-center gap-2">
                 <span className="text-cyan-500">
                   <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                 </span>
                 Active Personalization Tracks
               </h2>
               <button className="px-3 py-1.5 rounded bg-cyan-500/10 text-cyan-400 text-xs font-semibold hover:bg-cyan-500/20 transition-colors">View All</button>
             </div>
             
             <div className="space-y-4 relative z-10">
               {ACTIVE_ENGINES.map((track, idx) => (
                 <div key={idx} className="p-5 rounded-2xl bg-cyan-950/30 border border-cyan-500/20 hover:bg-cyan-900/40 transition-colors">
                   <div className="flex items-start justify-between mb-3">
                     <div>
                       <h3 className="font-semibold text-slate-200">{track.student}</h3>
                       <p className="text-sm text-cyan-400 font-medium">{track.focus}</p>
                     </div>
                     <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                       {track.phase}
                     </span>
                   </div>
                   
                   <div className="mt-4">
                     <div className="flex justify-between text-xs mb-1">
                       <span className="text-slate-400">Track Progress</span>
                       <span className="text-cyan-400 font-bold">{track.progress}%</span>
                     </div>
                     <div className="w-full bg-slate-800 rounded-full h-1.5">
                       <div className="bg-gradient-to-r from-cyan-500 to-blue-500 h-1.5 rounded-full relative" style={{width: `${track.progress}%`}}>
                         {track.progress < 100 && (
                           <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full shadow-[0_0_8px_rgba(6,182,212,0.8)]"></div>
                         )}
                       </div>
                     </div>
                   </div>
                 </div>
               ))}
             </div>
          </div>
        </div>
      ) : activeTab === 'BRAND' ? (
        <div className="mx-auto max-w-4xl space-y-6">
           <div className="rounded-3xl border border-blue-500/20 bg-gradient-to-b from-blue-950/20 to-slate-900/40 p-8 shadow-2xl relative overflow-hidden">
             <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/5 blur-[80px] rounded-full"></div>
             
             <div className="relative z-10 flex flex-col items-center text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-4 text-blue-400">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Deploy Institute Layouts</h2>
                <p className="text-slate-400 text-sm max-w-lg">Customize the graphical presentation of LMS components restricted exclusively under your tenant firewall map.</p>
             </div>

             <div className="space-y-6 relative z-10">
                <div>
                   <label className="block text-sm font-medium text-slate-300 mb-2">Display Foundation Name</label>
                   <input type="text" value={schoolName} onChange={(e) => setSchoolName(e.target.value)} placeholder="e.g., Delhi Public School Core" className="w-full bg-slate-950/50 border border-slate-700 focus:border-blue-500 rounded-xl px-4 py-3 text-white transition-colors"/>
                </div>
                
                <div>
                   <label className="block text-sm font-medium text-slate-300 mb-2">Global UI Hex Accent Color</label>
                   <div className="flex gap-4">
                     <div className="w-12 h-12 rounded-xl border border-slate-700 shrink-0 overflow-hidden shadow-inner">
                       <input type="color" value={themeColor} onChange={(e) => setThemeColor(e.target.value)} className="w-16 h-16 -m-2 cursor-pointer" />
                     </div>
                     <input type="text" value={themeColor} onChange={(e) => setThemeColor(e.target.value)} placeholder="#3b82f6" className="w-full bg-slate-950/50 border border-slate-700 focus:border-blue-500 rounded-xl px-4 py-3 text-white transition-colors uppercase font-mono"/>
                   </div>
                </div>

                <div>
                   <label className="block text-sm font-medium text-slate-300 mb-2">Header Logomark Hyperlink</label>
                   <div className="flex gap-2">
                     <input 
                       type="text" 
                       value={logoUrl} 
                       onChange={(e) => setLogoUrl(e.target.value)} 
                       placeholder="https://your-server.com/assets/logo.png" 
                       className="flex-1 bg-slate-950/50 border border-slate-700 focus:border-blue-500 rounded-xl px-4 py-3 text-white transition-colors"
                     />
                     <label className="cursor-pointer p-3 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/20 transition-all flex items-center justify-center group">
                       <input 
                         type="file" 
                         className="hidden" 
                         accept="image/*" 
                         onChange={handleLogoUpload} 
                       />
                       <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="group-hover:scale-110 transition-transform">
                         <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                         <polyline points="17 8 12 3 7 8"></polyline>
                         <line x1="12" y1="3" x2="12" y2="15"></line>
                       </svg>
                     </label>
                   </div>
                   <p className="text-xs text-slate-500 mt-2">Provides the standard image 24px height rendering. Leave empty to use PenSense stock.</p>
                </div>

                {/* Geofencing Configuration */}
                <div className="pt-6 mt-6 border-t border-slate-800/80">
                   <h3 className="text-sm font-bold text-blue-400 uppercase tracking-widest mb-4">Attendance Geofencing (50m Radius)</h3>
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     <div>
                       <label className="block text-sm font-medium text-slate-300 mb-1.5">Institute Latitude</label>
                       <input type="number" step="any" value={latitude} onChange={(e) => setLatitude(e.target.value)} placeholder="e.g. 19.0760" className="w-full bg-slate-950/50 border border-slate-700 focus:border-blue-500 rounded-xl px-4 py-3 text-white transition-colors font-mono"/>
                     </div>
                     <div>
                       <label className="block text-sm font-medium text-slate-300 mb-1.5">Institute Longitude</label>
                       <input type="number" step="any" value={longitude} onChange={(e) => setLongitude(e.target.value)} placeholder="e.g. 72.8777" className="w-full bg-slate-950/50 border border-slate-700 focus:border-blue-500 rounded-xl px-4 py-3 text-white transition-colors font-mono"/>
                     </div>
                   </div>
                   <button 
                     onClick={() => {
                       if (navigator.geolocation) {
                         toast.info("Accessing precise location...");
                         navigator.geolocation.getCurrentPosition(
                           (pos) => {
                             setLatitude(pos.coords.latitude);
                             setLongitude(pos.coords.longitude);
                             toast.success("Coordinates updated to current location!");
                           },
                           (err) => toast.error("Failed to get location. Please enter manually.")
                         );
                       }
                     }}
                     className="mt-4 text-xs font-bold text-blue-400 hover:text-blue-300 flex items-center gap-2 transition-colors"
                   >
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                     Set to my current location
                   </button>
                </div>

                <div className="pt-6 mt-6 border-t border-slate-800/80">
                   <button onClick={saveBrandingParams} disabled={isSaving} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-blue-900/20 transition-all active:scale-[0.98]">
                     {isSaving ? "Persisting Topology..." : "Deploy Config to Domain Registry"}
                   </button>
                </div>
             </div>
           </div>
        </div>
      ) : (
         <div className="mx-auto max-w-4xl space-y-6">
            <div className="rounded-3xl border border-emerald-500/20 bg-gradient-to-b from-emerald-950/20 to-slate-900/40 p-8 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/5 blur-[80px] rounded-full"></div>
              
              <div className="relative z-10 flex flex-col items-center text-center mb-8">
                 <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4 text-emerald-400">
                   <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect><line x1="1" y1="10" x2="23" y2="10"></line></svg>
                 </div>
                 <h2 className="text-2xl font-bold text-white mb-2">Payment gateway & GST Config</h2>
                 <p className="text-slate-400 text-sm max-w-lg">Enable online fee collection and automated tax invoice generation for your institute.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 relative z-10">
                 <div className="md:col-span-2">
                    <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-widest mb-4">Razorpay Credentials</h3>
                 </div>
                 <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Razorpay Key ID</label>
                    <input type="text" value={razorpayKeyId} onChange={(e) => setRazorpayKeyId(e.target.value)} placeholder="rzp_test_..." className="w-full bg-slate-950/50 border border-slate-700 focus:border-emerald-500 rounded-xl px-4 py-3 text-white transition-colors"/>
                 </div>
                 <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Razorpay Key Secret</label>
                    <input type="password" value={razorpayKeySecret} onChange={(e) => setRazorpayKeySecret(e.target.value)} placeholder="••••••••••••" className="w-full bg-slate-950/50 border border-slate-700 focus:border-emerald-500 rounded-xl px-4 py-3 text-white transition-colors"/>
                 </div>

                 <div className="md:col-span-2 pt-4">
                    <h3 className="text-sm font-bold text-emerald-400 uppercase tracking-widest mb-4">GST & Billing Details</h3>
                 </div>
                 <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Institute GSTIN</label>
                    <input type="text" value={gstNumber} onChange={(e) => setGstNumber(e.target.value)} placeholder="27ABCDE1234F1Z5" className="w-full bg-slate-950/50 border border-slate-700 focus:border-emerald-500 rounded-xl px-4 py-3 text-white transition-colors uppercase"/>
                 </div>
                 <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Institute PAN</label>
                    <input type="text" value={panNumber} onChange={(e) => setPanNumber(e.target.value)} placeholder="ABCDE1234F" className="w-full bg-slate-950/50 border border-slate-700 focus:border-emerald-500 rounded-xl px-4 py-3 text-white transition-colors uppercase"/>
                 </div>
                 <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">SAC Code</label>
                    <input type="text" value={sacCode} onChange={(e) => setSacCode(e.target.value)} placeholder="9992" className="w-full bg-slate-950/50 border border-slate-700 focus:border-emerald-500 rounded-xl px-4 py-3 text-white transition-colors uppercase"/>
                 </div>
                 <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Invoice Prefix</label>
                    <input type="text" value={invoicePrefix} onChange={(e) => setInvoicePrefix(e.target.value)} placeholder="PS" className="w-full bg-slate-950/50 border border-slate-700 focus:border-emerald-500 rounded-xl px-4 py-3 text-white transition-colors uppercase"/>
                 </div>
                 <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-300 mb-2">Full Institute Address (for Invoices)</label>
                    <textarea value={instituteAddress} onChange={(e) => setInstituteAddress(e.target.value)} placeholder="Floor 4, Knowledge Park, Mumbai..." className="w-full h-24 bg-slate-950/50 border border-slate-700 focus:border-emerald-500 rounded-xl px-4 py-3 text-white transition-colors resize-none"/>
                 </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">State Code</label>
                    <input type="text" value={stateCode} onChange={(e) => setStateCode(e.target.value)} placeholder="27 (Maharashtra)" className="w-full bg-slate-950/50 border border-slate-700 focus:border-emerald-500 rounded-xl px-4 py-3 text-white transition-colors"/>
                 </div>
                 <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Institute Contact Email</label>
                    <input type="email" value={instituteEmail} onChange={(e) => setInstituteEmail(e.target.value)} placeholder="contact@institute.com" className="w-full bg-slate-950/50 border border-slate-700 focus:border-emerald-500 rounded-xl px-4 py-3 text-white transition-colors"/>
                 </div>
                 <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Institute Contact Phone</label>
                    <input type="text" value={institutePhone} onChange={(e) => setInstitutePhone(e.target.value)} placeholder="+91 98765 43210" className="w-full bg-slate-950/50 border border-slate-700 focus:border-emerald-500 rounded-xl px-4 py-3 text-white transition-colors"/>
                 </div>

                 <div className="md:col-span-2 pt-6 mt-6 border-t border-slate-800/80">
                    <button onClick={saveBrandingParams} disabled={isSaving} className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-emerald-900/20 transition-all active:scale-[0.98]">
                      {isSaving ? "Syncing Credentials..." : "Persist Payment Secure Maps"}
                    </button>
                 </div>
              </div>
            </div>
         </div>
      )}
    </div>
  );
}
